import { NgTemplateOutlet } from '@angular/common'
import { Component, EventEmitter, Input, OnInit, TrackByFunction } from '@angular/core'
import { SelectItem } from '@ftr/forms'
import {
  AnimatedRemoteComponent,
  ApiResult,
  ErrorRetryComponent,
  LineLoaderComponent,
  TableColumn,
  TableModule,
  VerticalBreakpoint,
  skipFutureNonSuccess,
  tapData,
  tapFailure,
} from '@ftr/foundation'
import { BehaviorSubject, Observable, switchMap } from 'rxjs'
import { LiveTableLayoutHeaderComponent } from './live-table-layout-header.component'

export interface LiveTableLayoutComponentFilterDefinition<T> {
  title: string
  defaultFilterValue: T
  filterOptions: Observable<SelectItem<T>[]>
  filterKey?: string
  setFilter: ($event: T, refresh: EventEmitter<void>) => void
  singular?: string
  pageIdentifier?: string
}

/**
 * Similar to ListLayoutComponent but has several key differences
 * - No pagination
 * - No filters
 * - Live updates
 * - Handles refresh internally
 * - Filters out errors/loading states after the first success
 * - Has ViewEncapsulation Enabled
 * - Uses our standard column sizes
 */
@Component({
  standalone: true,
  selector: 'ftr-live-table-layout',
  templateUrl: './live-table-layout.component.html',
  imports: [
    AnimatedRemoteComponent,
    LineLoaderComponent,
    ErrorRetryComponent,
    TableModule,
    NgTemplateOutlet,
    LiveTableLayoutHeaderComponent,
  ],
})
export class LiveTableLayoutComponent<T, FilterType extends string = string> implements OnInit {
  /**
   * The page title
   */
  @Input()
  title: string

  /**
   * How to refer to more than one item in the list
   */
  @Input() plural: string

  /**
   * How to refer to one item in the list
   */
  @Input() singular: string

  /**
   * How long between automatic updates
   */
  @Input() updateIntervalMs: number

  /**
   * How to get the entities on refresh
   */
  @Input() entitiesRetriever: () => ApiResult<T[]>

  /**
   * Short description of what this table is
   */
  @Input() summary: string

  /**
   * The column layout for this table
   */
  @Input() columns: TableColumn<T>[]

  /**
   * How to detect duplicate rows, prevents complete page refreshes
   */
  @Input() trackBy: TrackByFunction<T>

  /**
   * At what point we switch to vertical cards, rather than horizontal rows
   */
  @Input() verticalBreakpoint: VerticalBreakpoint = 'small'

  /**
   * Whether scrolling the page should pause automatic updates.
   * Useful when you have a large list and dont want the user to lose their place
   */
  @Input() pauseUpdatesOnScroll = false

  /**
   * The filter definition to filter the records in the table
   */
  @Input() filter?: LiveTableLayoutComponentFilterDefinition<FilterType>

  entities: ApiResult<T[]>

  refresh: BehaviorSubject<void> = new BehaviorSubject<void>(undefined)

  isSuccess: boolean = false

  ngOnInit(): void {
    this.entities = this.refresh.pipe(
      switchMap(() => this.entitiesRetriever()),
      tapData(() => (this.isSuccess = true)),
      tapFailure(() => (this.isSuccess = false)),
      skipFutureNonSuccess(),
    )
  }
}
